/**
 * The signed-in admin's own session and password — `GET|DELETE /v1/auth/sessions`,
 * `POST /v1/auth/logout-all`, `POST /v1/auth/admin/password`.
 *
 * One hook module per backend router, per `admin/CLAUDE.md`. Mutation hooks own their own
 * invalidation, so a caller cannot forget it and cannot invalidate almost the right thing.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type SessionItem = components['schemas']['SessionItem'];
type SessionList = components['schemas']['SessionListResponse'];

export function useOwnSessions() {
  return useQuery({
    queryKey: keys.ownSessions(),
    queryFn: () => authedClient.request<SessionList>('/v1/auth/sessions'),
  });
}

export function useRevokeSession() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => authedClient.request<void>(`/v1/auth/sessions/${id}`, { method: 'DELETE' }),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.ownSessions() }),
  });
}

export function useSignOutOtherDevices() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => authedClient.request<void>('/v1/auth/logout-all', { method: 'POST' }),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.ownSessions() }),
  });
}

export interface PasswordChange {
  current_password: string;
  new_password: string;
  totp_code: string;
}

/**
 * Changing a password needs **both factors again** — the current password and a live code. That is
 * the server's rule and it is the right one: a walked-away-from console should not be a password
 * change. The server also revokes the other sessions on success, which is why this invalidates the
 * session list rather than leaving a stale one on screen.
 */
export function useChangePassword() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: PasswordChange) =>
      authedClient.request<void>('/v1/auth/admin/password', { method: 'POST', body }),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.ownSessions() }),
  });
}

export type AdminMe = components['schemas']['AdminMeOut'];
export type ProfileUpdate = components['schemas']['AdminMeUpdateIn'];
export type NotificationTopic = components['schemas']['NotificationTopicOut'];
type NotificationPrefs = components['schemas']['NotificationPreferencesOut'];
type ReEnrolStart = components['schemas']['TotpEnrolStartOut'];
type ReEnrolConfirm = components['schemas']['TotpEnrolConfirmOut'];
type RecoveryStatus = components['schemas']['RecoveryCodeStatusOut'];
type RecoveryCodes = components['schemas']['RecoveryCodesOut'];
type DeactivationRequested = components['schemas']['DeactivationRequestOut'];

/**
 * The signed-in admin's own row.
 *
 * Read as a query rather than off the auth store: the store holds what the menu needs (name,
 * scopes, enrolment status) and is hydrated once at sign-in, so an edit made here would not show
 * up in it until the next reload. `full_name` is not editable through this screen — only a
 * superadmin changes it — so nothing the form writes can make the top bar stale.
 */
export function useMe() {
  return useQuery({
    queryKey: keys.me(),
    queryFn: () => authedClient.request<AdminMe>('/v1/admin/me'),
  });
}

/**
 * Save the profile.
 *
 * ⛔ **Send only what changed.** The server treats an omitted field as "leave it alone" and an
 * explicit `null` as "clear it", so posting the whole form would overwrite fields the operator
 * never touched — and would make clearing one impossible to distinguish from ignoring it.
 */
export function useUpdateProfile() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: ProfileUpdate) =>
      authedClient.request<AdminMe>('/v1/admin/me', { method: 'PATCH', body }),
    onSuccess: (me) => client.setQueryData(keys.me(), me),
  });
}

/**
 * Which alerts reach this admin.
 *
 * ⛔ `is_locked` and `locked_reason` come from the SERVER and are rendered, never decided here.
 * They used to be a `'locked'` string in a fixture in this repo, which meant the guarantee that a
 * safety page and an audit-chain break reach every admin was one console build away from being
 * switched off.
 */
export function useNotificationPrefs() {
  return useQuery({
    queryKey: keys.meNotifications(),
    queryFn: () => authedClient.request<NotificationPrefs>('/v1/admin/me/notifications'),
  });
}

export function useSaveNotificationPrefs() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (topics: Record<string, boolean>) =>
      authedClient.request<NotificationPrefs>('/v1/admin/me/notifications', {
        method: 'PUT',
        body: { topics },
      }),
    onSuccess: (prefs) => client.setQueryData(keys.meNotifications(), prefs),
  });
}

export interface ReEnrolStartBody {
  current_password: string;
  totp_code: string;
}

/**
 * Begin moving the authenticator to a new device.
 *
 * ⛔ This is **not** `/v1/auth/admin/totp/enrol/start`. That one authenticates on the short-lived
 * token a login hands back when enrolment is still required — it is the first-enrolment path, and
 * an already-enrolled admin cannot reach it at all. This one is session-authenticated and
 * re-proves both factors on the OLD authenticator first, which is what stops somebody sitting on
 * a walked-away-from console from swapping the second factor for their own.
 *
 * The old authenticator keeps working until `confirm` succeeds, so abandoning this flow costs
 * nothing.
 */
export function useStartReEnrol() {
  return useMutation({
    mutationFn: (body: ReEnrolStartBody) =>
      authedClient.request<ReEnrolStart>('/v1/admin/me/totp/re-enrol/start', {
        method: 'POST',
        body,
      }),
  });
}

/** Prove the new device. The server retires the old secret and ends every other session. */
export function useConfirmReEnrol() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (totp_code: string) =>
      authedClient.request<ReEnrolConfirm>('/v1/admin/me/totp/re-enrol/confirm', {
        method: 'POST',
        body: { totp_code },
      }),
    /* Every other session has just been revoked, so a session list on screen is now wrong. */
    onSuccess: () => client.invalidateQueries({ queryKey: keys.ownSessions() }),
  });
}

/**
 * How many recovery codes are still unspent.
 *
 * ⛔ A `COUNT(*)` on the server. The screen used to print "8 of 10 unused" from a fixture — a
 * number nothing produced, telling an operator how many escape hatches they had left on no
 * evidence.
 */
export function useRecoveryCodeStatus() {
  return useQuery({
    queryKey: keys.meRecoveryCodes(),
    queryFn: () => authedClient.request<RecoveryStatus>('/v1/admin/me/recovery-codes'),
  });
}

/**
 * Void the unused codes and mint a fresh set. Both factors again, for the reason a recovery code
 * is dangerous: it bypasses the second factor, so minting one is as sensitive as moving the
 * authenticator. The plaintext comes back here once and is never retrievable again.
 */
export function useRegenerateRecoveryCodes() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: ReEnrolStartBody) =>
      authedClient.request<RecoveryCodes>('/v1/admin/me/recovery-codes/regenerate', {
        method: 'POST',
        body,
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.meRecoveryCodes() }),
  });
}

/**
 * Ask a superadmin to deactivate this account. A request, not an action: nothing about the
 * account changes, and asking again while one is open returns the first.
 */
export function useRequestDeactivation() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (reason: string) =>
      authedClient.request<DeactivationRequested>('/v1/admin/me/deactivation-request', {
        method: 'POST',
        body: { reason },
      }),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.me() }),
  });
}
